import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Store as StoreIcon,
  LocationOn as LocationIcon,
  LocalShipping as ShipstationIcon,
  Refresh as RefreshIcon,
  Inventory as Cin7Icon,
  Warehouse as ShipheroIcon,
  Lightbulb as SuggestionIcon
} from '@mui/icons-material';
import {
  getStoreConfigs,
  createStoreConfig,
  updateStoreConfig,
  getPlatformLocationMappings,
  createPlatformLocationMapping,
  updatePlatformLocationMapping,
  getShipstationMappings,
  createShipstationMapping,
  updateShipstationMapping,
  getCin7BranchMappings,
  createCin7BranchMapping,
  updateCin7BranchMapping,
  getShipheroMappings,
  createShipheroMapping,
  updateShipheroMapping,
  getWarehouses,
  discoverStores
} from '../../services/inventoryService';

const DEDUCTION_SOURCES = [
  { value: 'shipstation', label: 'ShipStation (use userId to determine warehouse)' },
  { value: 'platform', label: 'Platform (use platform location/FBA warehouse)' }
];

const PLATFORMS = [
  { value: 'shopify', label: 'Shopify' },
  { value: 'amazon', label: 'Amazon' },
  { value: 'woocommerce', label: 'WooCommerce' }
];

function WarehouseMappings() {
  const [activeTab, setActiveTab] = useState(() => (
    new URLSearchParams(window.location.search).get('tab') === 'shiphero' ? 4 : 0
  ));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data
  const [storeConfigs, setStoreConfigs] = useState([]);
  const [availableStores, setAvailableStores] = useState([]);
  const [platformMappings, setPlatformMappings] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [shipstationMappings, setShipstationMappings] = useState([]);
  const [availableShipstationUsers, setAvailableShipstationUsers] = useState([]);
  const [cin7Mappings, setCin7Mappings] = useState([]);
  const [availableCin7Branches, setAvailableCin7Branches] = useState([]);
  const [unmappedCin7Branches, setUnmappedCin7Branches] = useState([]);
  const [shipheroMappings, setShipheroMappings] = useState([]);
  const [unmappedShipheroWarehouses, setUnmappedShipheroWarehouses] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Dialogs
  const [storeDialog, setStoreDialog] = useState({ open: false, data: null });
  const [platformDialog, setPlatformDialog] = useState({ open: false, data: null });
  const [shipstationDialog, setShipstationDialog] = useState({ open: false, data: null });
  const [cin7Dialog, setCin7Dialog] = useState({ open: false, data: null });
  const [shipheroDialog, setShipheroDialog] = useState({ open: false, data: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [storesRes, discoverRes, platformRes, shipstationRes, cin7Res, shipheroRes, warehousesRes] = await Promise.all([
        getStoreConfigs(),
        discoverStores(),
        getPlatformLocationMappings({ platform: 'shopify' }),
        getShipstationMappings(),
        getCin7BranchMappings(),
        getShipheroMappings().catch(err => { console.warn('Shiphero mappings unavailable:', err?.message); return null; }),
        getWarehouses({ is_active: true })
      ]);
      setStoreConfigs(storesRes?.data || []);
      setAvailableStores(discoverRes?.available_stores || []);
      setPlatformMappings(platformRes?.data || []);
      setAvailableLocations(platformRes?.available_locations || []);
      setShipstationMappings(shipstationRes?.data || []);
      setAvailableShipstationUsers(shipstationRes?.available_users || []);
      setCin7Mappings(cin7Res?.data || []);
      setAvailableCin7Branches(cin7Res?.available_branches || []);
      setUnmappedCin7Branches(cin7Res?.unmapped_branches || []);
      setShipheroMappings(shipheroRes?.data || []);
      setUnmappedShipheroWarehouses(shipheroRes?.unmapped_warehouses || []);
      setWarehouses(warehousesRes?.data || warehousesRes?.warehouses || []);
    } catch (err) {
      console.error('Failed to load mappings:', err);
      setError('Failed to load mappings data');
    } finally {
      setLoading(false);
    }
  };


  // Store Config handlers
  const handleSaveStoreConfig = async () => {
    try {
      const { data } = storeDialog;
      if (data.id) {
        await updateStoreConfig(data.id, data);
      } else {
        await createStoreConfig(data);
      }
      setSuccess('Store config saved successfully');
      setStoreDialog({ open: false, data: null });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save store config');
    }
  };

  // Platform Location handlers
  const handleSavePlatformMapping = async () => {
    try {
      const { data } = platformDialog;
      if (data.id) {
        await updatePlatformLocationMapping(data.id, data);
      } else {
        await createPlatformLocationMapping(data);
      }
      setSuccess('Platform location mapping saved');
      setPlatformDialog({ open: false, data: null });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save platform mapping');
    }
  };

  // ShipStation handlers
  const handleSaveShipstationMapping = async () => {
    try {
      const { data } = shipstationDialog;
      if (data.id) {
        await updateShipstationMapping(data.id, data);
      } else {
        await createShipstationMapping(data);
      }
      setSuccess('ShipStation mapping saved');
      setShipstationDialog({ open: false, data: null });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save ShipStation mapping');
    }
  };

  // Cin7 Branch handlers
  const handleSaveCin7Mapping = async () => {
    try {
      const { data } = cin7Dialog;
      if (data.id) {
        await updateCin7BranchMapping(data.id, data);
      } else {
        await createCin7BranchMapping(data);
      }
      setSuccess('Cin7 branch mapping saved');
      setCin7Dialog({ open: false, data: null });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save Cin7 branch mapping');
    }
  };

  // Shiphero handlers
  const handleSaveShipheroMapping = async () => {
    try {
      const { data } = shipheroDialog;
      if (data.id) {
        await updateShipheroMapping(data.id, { warehouse_id: data.warehouse_id, is_active: data.is_active });
      } else {
        await createShipheroMapping({
          connector_id: data.connector_id,
          warehouse_id: data.warehouse_id,
          shiphero_warehouse_id: data.shiphero_warehouse_id,
          shiphero_warehouse_name: data.shiphero_warehouse_name
        });
      }
      setSuccess('Shiphero warehouse mapping saved');
      setShipheroDialog({ open: false, data: null });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to save Shiphero mapping');
    }
  };

  // `warehouses` is fetched with { is_active: true }, so any mapping pointing at a deactivated
  // warehouse fell through to the raw `ID: n`. Callers that have the resolved name on the row
  // itself (the Cin7 list already selects w.name / w.code, mappings-service.js:404) can pass it
  // as a fallback. Callers that don't pass it behave exactly as before.
  const getWarehouseName = (id, name, code) => {
    const wh = warehouses.find(w => w.id === id);
    if (wh) return `${wh.name} (${wh.code})`;
    if (name) return code ? `${name} (${code})` : name;
    return `ID: ${id}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Warehouse Mappings
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => {
            setActiveTab(v);
            const params = new URLSearchParams(window.location.search);
            if (v === 4) params.set('tab', 'shiphero');
            else params.delete('tab');
            const next = params.toString();
            window.history.replaceState(null, '', next ? `${window.location.pathname}?${next}` : window.location.pathname);
          }}
        >
          <Tab icon={<StoreIcon />} label="Store Configs" iconPosition="start" />
          <Tab icon={<LocationIcon />} label="Platform Locations" iconPosition="start" />
          <Tab icon={<ShipstationIcon />} label="ShipStation Users" iconPosition="start" />
          <Tab icon={<Cin7Icon />} label="Cin7 Branches" iconPosition="start" />
          <Tab icon={<ShipheroIcon />} label="Shiphero Warehouses" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Store Configs */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Store Deduction Source Configuration</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setError(null); setStoreDialog({ open: true, data: { store_identifier: '', store_name: '', store_platform: 'shopify', deduction_source: 'platform' } }); }}
              >
                Add Store
              </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Configure how inventory deductions are determined for each store. Choose "ShipStation" to deduct via ShipStation shipments (warehouse from ShipStation user mapping), or "Platform" to deduct via platform order data (warehouse resolved from Shopify location mappings at import time).
            </Typography>

            {/* Available Unconfigured Stores */}
            {availableStores.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {availableStores.length} Unconfigured Store{availableStores.length > 1 ? 's' : ''} Found
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The following stores from your connectors need configuration:
                </Typography>
                {availableStores.map((store, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {store.store_name} <Chip label={store.platform} size="small" sx={{ ml: 1 }} />
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Connector ID: {store.connector_id} • Suggested: {store.suggested_fulfillment_source}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => { setError(null); setStoreDialog({
                        open: true,
                        data: {
                          connector_id: store.connector_id,
                          store_identifier: store.store_identifier || store.store_name, // Use backend-provided identifier
                          store_name: store.store_name,
                          store_platform: store.platform,
                          deduction_source: store.suggested_fulfillment_source === 'shipstation' ? 'shipstation' : 'platform', // Map suggested source
                          warehouse_code: '',
                          track_inventory: 1,
                          auto_sync: 1,
                          is_active: 1
                        }
                      }); }}
                    >
                      Configure
                    </Button>
                  </Box>
                ))}
              </Alert>
            )}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Store</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>Deduction Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storeConfigs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No store configs yet. Add one to get started.</TableCell>
                    </TableRow>
                  ) : (
                    storeConfigs.map(cfg => (
                      <TableRow key={cfg.id}>
                        <TableCell>
                          <Typography fontWeight="medium">{cfg.store_name}</Typography>
                          <Typography variant="caption" color="textSecondary">{cfg.store_identifier}</Typography>
                        </TableCell>
                        <TableCell><Chip label={cfg.store_platform} size="small" /></TableCell>
                        <TableCell>
                          <Chip
                            label={cfg.deduction_source === 'shipstation' ? 'ShipStation' :
                                   (cfg.store_platform === 'amazon' || cfg.store_name?.includes('amazon')) ? 'Amazon FBA' : 'Platform'}
                            color={cfg.deduction_source === 'shipstation' ? 'primary' :
                                   (cfg.store_platform === 'amazon' || cfg.store_name?.includes('amazon')) ? 'warning' : 'secondary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={cfg.is_active ? 'Active' : 'Inactive'} color={cfg.is_active ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => { setError(null); setStoreDialog({ open: true, data: cfg }); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 1: Platform Locations */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Platform Location → Warehouse Mapping</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setError(null); setPlatformDialog({ open: true, data: { platform: 'shopify', platform_location_id: '', platform_location_name: '', warehouse_id: '', store_identifier: '' } }); }}
              >
                Add Mapping
              </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Map platform locations (e.g., Shopify locations) to your internal warehouses. When using "Platform" as the deduction source, fulfillments will deduct from the warehouse mapped to the platform location.
            </Typography>

            {/* Available Locations List - Only show for stores configured to use Platform deduction */}
            {(() => {
              // Filter locations to only show stores that use "platform" as deduction source
              const platformStoreIdentifiers = storeConfigs
                .filter(cfg => cfg.deduction_source === 'platform' && cfg.store_platform === 'shopify')
                .map(cfg => cfg.store_identifier);

              const filteredLocations = availableLocations.filter(loc =>
                platformStoreIdentifiers.some(identifier =>
                  loc.shop_id?.includes(identifier) || identifier?.includes(loc.shop_id)
                )
              );

              return filteredLocations.length > 0 && (
              <Box sx={{ mb: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Available Shopify Locations (Detected)</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  These locations are from stores configured to use Platform-based deduction. Click "Map" to link them to a warehouse.
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Store</TableCell>
                        <TableCell>Location Name</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLocations.map((loc) => {
                        const isMapped = platformMappings.some(m => m.platform_location_id === loc.location_id);
                        const address = loc.address || {};
                        const addressStr = [address.address1, address.city, address.country].filter(Boolean).join(', ');

                        return (
                          <TableRow key={loc.location_id}>
                            <TableCell>{loc.shop_id}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{loc.name}</Typography>
                              <Typography variant="caption" color="textSecondary">ID: {loc.location_id}</Typography>
                            </TableCell>
                            <TableCell>{addressStr || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={isMapped ? 'Mapped' : 'Unmapped'}
                                color={isMapped ? 'success' : 'default'}
                                size="small"
                                variant={isMapped ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {!isMapped && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => { setError(null); setPlatformDialog({
                                    open: true,
                                    data: {
                                      platform: 'shopify',
                                      platform_location_id: loc.location_id,
                                      platform_location_name: loc.name,
                                      warehouse_id: '',
                                      store_identifier: loc.shop_id
                                    }
                                  }); }}
                                >
                                  Map
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              );
            })()}

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Configured Mappings</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Platform</TableCell>
                    <TableCell>Location ID</TableCell>
                    <TableCell>Location Name</TableCell>
                    <TableCell>→ Warehouse</TableCell>
                    <TableCell>Store</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {platformMappings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No platform location mappings yet.</TableCell>
                    </TableRow>
                  ) : (
                    platformMappings.map(m => (
                      <TableRow key={m.id}>
                        <TableCell><Chip label={m.platform} size="small" /></TableCell>
                        <TableCell><code>{m.platform_location_id}</code></TableCell>
                        <TableCell>{m.platform_location_name || '-'}</TableCell>
                        <TableCell><Chip label={getWarehouseName(m.warehouse_id)} color="info" size="small" /></TableCell>
                        <TableCell>{m.store_identifier || '-'}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => { setError(null); setPlatformDialog({ open: true, data: m }); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: ShipStation Users */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">ShipStation User → Warehouse Mapping</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setError(null); setShipstationDialog({ open: true, data: { shipstation_user_id: '', shipstation_user_name: '', warehouse_id: '' } }); }}
              >
                Add Mapping
              </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Map ShipStation user IDs to your internal warehouses. When using "ShipStation" as the deduction source, the userId in the shipment data determines which warehouse to deduct inventory from.
            </Typography>

            {/* Available ShipStation Users List */}
            {availableShipstationUsers.length > 0 && (
              <Box sx={{ mb: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Available ShipStation Users (Detected)</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  These users were found in your connected ShipStation account. Click "Map" to link them to a warehouse.
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>User ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {availableShipstationUsers.map((user) => {
                        const isMapped = shipstationMappings.some(m => m.shipstation_user_id === user.user_id);

                        return (
                          <TableRow key={user.user_id}>
                            <TableCell><code>{user.user_id}</code></TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{user.user_name}</Typography>
                              {user.name && user.name !== user.user_name && (
                                <Typography variant="caption" color="textSecondary">{user.name}</Typography>
                              )}
                            </TableCell>
                            <TableCell>{user.email || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={isMapped ? 'Mapped' : 'Unmapped'}
                                color={isMapped ? 'success' : 'default'}
                                size="small"
                                variant={isMapped ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {!isMapped && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => { setError(null); setShipstationDialog({
                                    open: true,
                                    data: {
                                      shipstation_user_id: user.user_id,
                                      shipstation_user_name: user.user_name,
                                      warehouse_id: ''
                                    }
                                  }); }}
                                >
                                  Map
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Configured Mappings</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ShipStation User ID</TableCell>
                    <TableCell>User Name</TableCell>
                    <TableCell>→ Warehouse</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipstationMappings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No ShipStation user mappings yet.</TableCell>
                    </TableRow>
                  ) : (
                    shipstationMappings.map(m => (
                      <TableRow key={m.id}>
                        <TableCell><code>{m.shipstation_user_id}</code></TableCell>
                        <TableCell>{m.shipstation_user_name || '-'}</TableCell>
                        <TableCell><Chip label={getWarehouseName(m.warehouse_id)} color="info" size="small" /></TableCell>
                        <TableCell>
                          <Chip label={m.is_active ? 'Active' : 'Inactive'} color={m.is_active ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => { setError(null); setShipstationDialog({ open: true, data: m }); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 3: Cin7 Branches */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Cin7 Branch → Warehouse Mapping</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setError(null); setCin7Dialog({ open: true, data: { cin7_branch_id: '', cin7_branch_name: '', cin7_country_code: '', warehouse_id: '' } }); }}
              >
                Add Mapping
              </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Map Cin7 branches (warehouses) to your internal warehouses. This is used when syncing Cin7 wholesale orders to determine which internal warehouse to assign.
            </Typography>

            {/* Unmapped Cin7 Branches with Suggestions */}
            {unmappedCin7Branches.length > 0 && (
              <Box sx={{ mb: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Unmapped Cin7 Branches ({unmappedCin7Branches.length})
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  These Cin7 branches have not been mapped to internal warehouses. Click "Map" to configure them.
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Branch ID</TableCell>
                        <TableCell>Branch Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Orders</TableCell>
                        <TableCell>Country</TableCell>
                        <TableCell>Suggested Warehouse</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmappedCin7Branches
                        .sort((a, b) => (b.order_count || 0) - (a.order_count || 0))
                        .map((branch) => (
                        <TableRow
                          key={branch.branch_id}
                          sx={{
                            opacity: branch.is_active === false ? 0.7 : 1,
                            bgcolor: branch.order_count > 0 ? 'action.hover' : 'inherit'
                          }}
                        >
                          <TableCell><code>{branch.branch_id}</code></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{branch.branch_name}</Typography>
                            <Typography variant="caption" color="textSecondary">{branch.branch_type}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={branch.is_active === false ? 'Inactive' : 'Active'}
                              size="small"
                              color={branch.is_active === false ? 'default' : 'success'}
                              variant={branch.is_active === false ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={branch.order_count > 0 ? 'bold' : 'normal'}
                              color={branch.order_count > 0 ? 'primary' : 'textSecondary'}
                            >
                              {branch.order_count || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {branch.country_code && (
                              <Chip label={branch.country_code} size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            {branch.suggested_warehouse ? (
                              <Tooltip title={`Match type: ${branch.suggested_warehouse.match_type}`}>
                                <Chip
                                  icon={<SuggestionIcon fontSize="small" />}
                                  label={`${branch.suggested_warehouse.warehouse_name} (${branch.suggested_warehouse.warehouse_code})`}
                                  color="success"
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="textSecondary">No suggestion</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant={branch.order_count > 0 ? "contained" : "outlined"}
                              color={branch.order_count > 0 ? "primary" : "inherit"}
                              onClick={() => { setError(null); setCin7Dialog({
                                open: true,
                                data: {
                                  cin7_branch_id: branch.branch_id,
                                  cin7_branch_name: branch.branch_name,
                                  cin7_country_code: branch.country_code || '',
                                  warehouse_id: branch.suggested_warehouse?.warehouse_id || ''
                                }
                              }); }}
                            >
                              Map
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Configured Mappings</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cin7 Branch ID</TableCell>
                    <TableCell>Branch Name</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>→ Warehouse</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cin7Mappings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No Cin7 branch mappings yet. Configure mappings above to sync Cin7 wholesale orders.</TableCell>
                    </TableRow>
                  ) : (
                    cin7Mappings.map(m => (
                      <TableRow key={m.id}>
                        <TableCell><code>{m.cin7_branch_id}</code></TableCell>
                        <TableCell>{m.cin7_branch_name || '-'}</TableCell>
                        <TableCell>
                          {m.cin7_country_code && (
                            <Chip label={m.cin7_country_code} size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell><Chip label={getWarehouseName(m.warehouse_id, m.warehouse_name, m.warehouse_code)} color="info" size="small" /></TableCell>
                        <TableCell>
                          <Chip label={m.is_active ? 'Active' : 'Inactive'} color={m.is_active ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => { setError(null); setCin7Dialog({ open: true, data: m }); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 4: Shiphero Warehouses */}
      {activeTab === 4 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Shiphero Warehouse → Warehouse Mapping</Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Map Shiphero warehouses (per connector) to your internal warehouses. Used by the inventory pipeline to attribute Shiphero shipments, inventory changes (cycle counts, write-offs), and returns to the correct internal warehouse.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Stock calculations pick up ShipHero adjustments only when the ShipHero row is a mapped manual cycle count amendment:
              {' '}<strong>change_type=cycle_count</strong>, <strong>reference_type=manual</strong>, and reason contains <strong>Ad-hoc count and amendment</strong>.
              Active mappings decide which internal warehouse receives the adjustment; inactive or unmapped ShipHero warehouses are ignored.
            </Alert>

            {/* Unmapped Shiphero warehouses */}
            {unmappedShipheroWarehouses.length > 0 && (
              <Box sx={{ mb: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Unmapped Shiphero Warehouses ({unmappedShipheroWarehouses.length})
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  These Shiphero warehouses (detected from inventory snapshots) have not yet been linked to an internal warehouse. Click "Map" to include matching ShipHero adjustments in stock calculations.
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Connector</TableCell>
                        <TableCell>Shiphero Warehouse Name</TableCell>
                        <TableCell>Shiphero Warehouse ID</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmappedShipheroWarehouses.map((wh) => (
                        <TableRow key={`${wh.connector_id}::${wh.shiphero_warehouse_id}`}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{wh.connector_name}</Typography>
                            <Typography variant="caption" color="textSecondary">ID: {wh.connector_id}</Typography>
                          </TableCell>
                          <TableCell>{wh.shiphero_warehouse_name || <em>(no name)</em>}</TableCell>
                          <TableCell><code style={{ fontSize: '0.75rem' }}>{wh.shiphero_warehouse_id}</code></TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => { setError(null); setShipheroDialog({
                                open: true,
                                data: {
                                  connector_id: wh.connector_id,
                                  shiphero_warehouse_id: wh.shiphero_warehouse_id,
                                  shiphero_warehouse_name: wh.shiphero_warehouse_name || '',
                                  warehouse_id: ''
                                }
                              }); }}
                            >
                              Map
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Configured Mappings</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Connector</TableCell>
                    <TableCell>Shiphero Warehouse</TableCell>
                    <TableCell>→ Internal Warehouse</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipheroMappings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No Shiphero warehouse mappings yet. Use the "Map" buttons above (after a Shiphero sync has populated inventory snapshots).</TableCell>
                    </TableRow>
                  ) : (
                    shipheroMappings.map(m => (
                      <TableRow key={m.id}>
                        <TableCell>
                          {m.connector_name || `Connector ${m.connector_id}`}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{m.shiphero_warehouse_name || '-'}</Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>{m.shiphero_warehouse_id}</Typography>
                        </TableCell>
                        <TableCell><Chip label={getWarehouseName(m.warehouse_id)} color="info" size="small" /></TableCell>
                        <TableCell>
                          <Chip label={m.is_active ? 'Active' : 'Inactive'} color={m.is_active ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => { setError(null); setShipheroDialog({ open: true, data: m }); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Store Config Dialog */}
      <Dialog open={storeDialog.open} onClose={() => setStoreDialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{storeDialog.data?.id ? 'Edit Store Config' : 'Add Store Config'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Store Identifier"
              value={storeDialog.data?.store_identifier || ''}
              onChange={(e) => setStoreDialog(prev => ({ ...prev, data: { ...prev.data, store_identifier: e.target.value } }))}
              placeholder="e.g., mystore.myshopify.com"
              fullWidth
              disabled={!!storeDialog.data?.id}
            />
            <TextField
              label="Store Name"
              value={storeDialog.data?.store_name || ''}
              onChange={(e) => setStoreDialog(prev => ({ ...prev, data: { ...prev.data, store_name: e.target.value } }))}
              placeholder="Friendly name for the store"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={storeDialog.data?.store_platform || 'shopify'}
                label="Platform"
                onChange={(e) => setStoreDialog(prev => ({ ...prev, data: { ...prev.data, store_platform: e.target.value } }))}
                disabled={!!storeDialog.data?.id}
              >
                {PLATFORMS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Deduction Source</InputLabel>
              <Select
                value={storeDialog.data?.deduction_source || 'platform'}
                label="Deduction Source"
                onChange={(e) => setStoreDialog(prev => ({ ...prev, data: { ...prev.data, deduction_source: e.target.value } }))}
              >
                {DEDUCTION_SOURCES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStoreDialog({ open: false, data: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveStoreConfig}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Platform Location Dialog */}
      <Dialog open={platformDialog.open} onClose={() => setPlatformDialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{platformDialog.data?.id ? 'Edit Platform Location Mapping' : 'Add Platform Location Mapping'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platformDialog.data?.platform || 'shopify'}
                label="Platform"
                onChange={(e) => setPlatformDialog(prev => ({ ...prev, data: { ...prev.data, platform: e.target.value } }))}
              >
                {PLATFORMS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Platform Location ID"
              value={platformDialog.data?.platform_location_id || ''}
              onChange={(e) => setPlatformDialog(prev => ({ ...prev, data: { ...prev.data, platform_location_id: e.target.value } }))}
              placeholder="e.g., Shopify location ID"
              fullWidth
            />
            <TextField
              label="Location Name (optional)"
              value={platformDialog.data?.platform_location_name || ''}
              onChange={(e) => setPlatformDialog(prev => ({ ...prev, data: { ...prev.data, platform_location_name: e.target.value } }))}
              placeholder="Friendly name for reference"
              fullWidth
            />
            <TextField
              label="Store Identifier (optional)"
              value={platformDialog.data?.store_identifier || ''}
              onChange={(e) => setPlatformDialog(prev => ({ ...prev, data: { ...prev.data, store_identifier: e.target.value } }))}
              placeholder="Associate with a specific store"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">Maps to Warehouse</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={String(platformDialog.data?.warehouse_id || '')}
                label="Maps to Warehouse"
                onChange={(e) => setPlatformDialog(prev => ({ ...prev, data: { ...prev.data, warehouse_id: e.target.value } }))}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {warehouses.map(w => (
                  <MenuItem key={w.id} value={String(w.id)}>
                    {w.name} ({w.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlatformDialog({ open: false, data: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePlatformMapping}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ShipStation User Dialog */}
      <Dialog open={shipstationDialog.open} onClose={() => setShipstationDialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{shipstationDialog.data?.id ? 'Edit ShipStation User Mapping' : 'Add ShipStation User Mapping'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="ShipStation User ID"
              value={shipstationDialog.data?.shipstation_user_id || ''}
              onChange={(e) => setShipstationDialog(prev => ({ ...prev, data: { ...prev.data, shipstation_user_id: e.target.value } }))}
              placeholder="e.g., 515574"
              fullWidth
            />
            <TextField
              label="User Name (optional)"
              value={shipstationDialog.data?.shipstation_user_name || ''}
              onChange={(e) => setShipstationDialog(prev => ({ ...prev, data: { ...prev.data, shipstation_user_name: e.target.value } }))}
              placeholder="Friendly name for reference"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="ss-warehouse-select-label">Maps to Warehouse</InputLabel>
              <Select
                labelId="ss-warehouse-select-label"
                value={String(shipstationDialog.data?.warehouse_id || '')}
                label="Maps to Warehouse"
                onChange={(e) => setShipstationDialog(prev => ({ ...prev, data: { ...prev.data, warehouse_id: e.target.value } }))}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {warehouses.map(w => (
                  <MenuItem key={w.id} value={String(w.id)}>
                    {w.name} ({w.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipstationDialog({ open: false, data: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveShipstationMapping}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Cin7 Branch Dialog */}
      <Dialog open={cin7Dialog.open} onClose={() => setCin7Dialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{cin7Dialog.data?.id ? 'Edit Cin7 Branch Mapping' : 'Add Cin7 Branch Mapping'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Cin7 Branch ID"
              value={cin7Dialog.data?.cin7_branch_id || ''}
              onChange={(e) => setCin7Dialog(prev => ({ ...prev, data: { ...prev.data, cin7_branch_id: e.target.value } }))}
              placeholder="e.g., 6230"
              fullWidth
              disabled={!!cin7Dialog.data?.id}
            />
            <TextField
              label="Branch Name"
              value={cin7Dialog.data?.cin7_branch_name || ''}
              onChange={(e) => setCin7Dialog(prev => ({ ...prev, data: { ...prev.data, cin7_branch_name: e.target.value } }))}
              placeholder="e.g., AU_G3PL"
              fullWidth
            />
            <TextField
              label="Country Code (optional)"
              value={cin7Dialog.data?.cin7_country_code || ''}
              onChange={(e) => setCin7Dialog(prev => ({ ...prev, data: { ...prev.data, cin7_country_code: e.target.value } }))}
              placeholder="e.g., AU, US, UK, EU"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="cin7-warehouse-select-label">Maps to Warehouse</InputLabel>
              <Select
                labelId="cin7-warehouse-select-label"
                value={String(cin7Dialog.data?.warehouse_id || '')}
                label="Maps to Warehouse"
                onChange={(e) => setCin7Dialog(prev => ({ ...prev, data: { ...prev.data, warehouse_id: e.target.value } }))}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {warehouses.map(w => (
                  <MenuItem key={w.id} value={String(w.id)}>
                    {w.name} ({w.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCin7Dialog({ open: false, data: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCin7Mapping}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Shiphero Warehouse Dialog */}
      <Dialog open={shipheroDialog.open} onClose={() => setShipheroDialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{shipheroDialog.data?.id ? 'Edit Shiphero Warehouse Mapping' : 'Map Shiphero Warehouse'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Connector"
              value={
                shipheroDialog.data?.connector_name ||
                (shipheroDialog.data?.connector_id ? `Connector ${shipheroDialog.data.connector_id}` : '')
              }
              fullWidth
              disabled
            />
            <TextField
              label="Shiphero Warehouse Name"
              value={shipheroDialog.data?.shiphero_warehouse_name || ''}
              fullWidth
              disabled
            />
            <TextField
              label="Shiphero Warehouse ID"
              value={shipheroDialog.data?.shiphero_warehouse_id || ''}
              fullWidth
              disabled
              InputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
            <FormControl fullWidth>
              <InputLabel id="shiphero-warehouse-select-label">Maps to Warehouse</InputLabel>
              <Select
                labelId="shiphero-warehouse-select-label"
                value={String(shipheroDialog.data?.warehouse_id || '')}
                label="Maps to Warehouse"
                onChange={(e) => setShipheroDialog(prev => ({ ...prev, data: { ...prev.data, warehouse_id: e.target.value } }))}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {warehouses.map(w => (
                  <MenuItem key={w.id} value={String(w.id)}>
                    {w.name} ({w.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {shipheroDialog.data?.id && (
              <FormControl fullWidth>
                <InputLabel id="shiphero-status-select-label">Status</InputLabel>
                <Select
                  labelId="shiphero-status-select-label"
                  value={shipheroDialog.data?.is_active ?? 1}
                  label="Status"
                  onChange={(e) => setShipheroDialog(prev => ({ ...prev, data: { ...prev.data, is_active: e.target.value } }))}
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipheroDialog({ open: false, data: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveShipheroMapping}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WarehouseMappings;
